import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigation } from '@/contexts/NavigationContext'
import { useSwipeGesture } from '@/hooks/useSwipeGesture'
import { BrandLogo } from '@/components/ui/BrandLogo'
import { BottomNav } from './BottomNav'
import { SideDrawer } from './SideDrawer'

/**
 * Native-app-like mobile / PWA shell with safe-area padding.
 */
export function MobileLayout({ children }: { children: ReactNode }) {
  const { t, i18n } = useTranslation('common')
  const { openDrawer, closeDrawer, drawerOpen } = useNavigation()
  const isRtl = i18n.dir() === 'rtl'

  const edgeSwipe = useSwipeGesture({
    onSwipeRight: () => {
      if (!isRtl) openDrawer()
      else if (drawerOpen) closeDrawer()
    },
    onSwipeLeft: () => {
      if (isRtl) openDrawer()
      else if (drawerOpen) closeDrawer()
    },
    threshold: 48,
  })

  return (
    <div
      className="flex h-dvh w-full flex-col overflow-hidden bg-terminal-bg text-terminal-fg"
      {...edgeSwipe}
    >
      <header
        className={[
          'flex h-12 shrink-0 items-center justify-between border-b border-terminal-border px-3',
          'pt-[env(safe-area-inset-top)]',
        ].join(' ')}
      >
        <button
          type="button"
          onClick={openDrawer}
          className="inline-flex h-10 min-h-touch w-10 items-center justify-center rounded text-terminal-muted hover:bg-terminal-surface"
          aria-label={t('drawer.open')}
        >
          <HamburgerIcon />
        </button>
        <BrandLogo height={22} />
        <span
          className="inline-flex h-2.5 w-2.5 rounded-full bg-bid"
          title={t('status.connected')}
        />
      </header>

      <main
        className={[
          'relative flex min-h-0 flex-1 flex-col overflow-hidden',
          'pb-[calc(4.25rem+env(safe-area-inset-bottom))]',
        ].join(' ')}
      >
        {children}
      </main>

      <BottomNav />
      <SideDrawer />
    </div>
  )
}

function HamburgerIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" aria-hidden>
      <path
        d="M4 7h16M4 12h16M4 17h16"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
      />
    </svg>
  )
}
